import React, { PureComponent } from "react";
import {
  Bar,
  Tooltip,
  ResponsiveContainer,
  XAxis,
  ComposedChart,
  YAxis,
  Line,
} from "recharts";
import Loader from "./../Loader/Loader.js";
import {
  Metric,
  selectFormat,
  MAIN_COLORS,
  CustomizedYAxisTickRight,
} from "components/Utils/Utils.js";
import Heading from "./../Elements/Heading";

class LeeSummaryMetrics extends PureComponent {
  widgetTitles = {
    open_rate: "Unique Open Rate",
    ctr: "Click-to-Open Rate",
    delivered_rate: "Unique Delivered Rate",
    converted_unique: "Unique Conversions",
  };

  metricsByDayLabels = {
    converted_unique: { email: "Conversions" },
    ctr: { email: "Clicks", ctr: "Click-To-Open Rate" },
    delivered_rate: {
      delivered_rate: "Unique Delivered Rate",
      email: "Deliveries",
    },
    open_rate: {
      open_rate: "Unique Open Rate",
      email: "Opens",
    },
  };

  componentDidMount() {
    this.props.childRef(this);
  }

  formatMetricKey = (key) => {
    return key
      .split("_")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(" ");
  };

  CustomTooltip = ({ active, payload, label, title }) => {
    if (active && payload && payload.length) {
      return (
        <div className="standard-tooltip">
          {label && (
            <header className="standard-tooltip__header">
              <span className="standard-tooltip__key-header">Date:</span>
              <span className="standard-tooltip__value-header">{label}</span>
            </header>
          )}
          <main className="standard-tooltip__main">
            {payload.map((entry, index) => {
              let metricName = entry.dataKey;

              return (
                <div className="standard-tooltip__item" key={`item-${index}`}>
                  <span
                    className="standard-tooltip__icon"
                    style={{ background: entry.color }}
                  ></span>
                  <span
                    className="standard-tooltip__key"
                    style={{ color: entry.color }}
                  >
                    {this.metricsByDayLabels[title][metricName]}
                  </span>
                  <span className="standard-tooltip__value">
                    {selectFormat(
                      ["_rate", "ctr"].find((m) => entry.name.includes(m))
                        ? "percentage"
                        : "number",
                      entry.value,
                      null
                    )}
                  </span>
                </div>
              );
            })}
          </main>
        </div>
      );
    }

    return null;
  };

  renderCharts = (metrics) => {
    const allCharts = [];
    metrics = [
      ...metrics.filter((m) => m.includes("email")),
      ...metrics.filter((m) => !m.includes("email")),
    ];
    metrics.forEach((metric) => {
      if (!metric.includes("email")) {
        allCharts.push(
          <Line
            key={metric}
            yAxisId="right"
            type="monotone"
            dataKey={metric}
            stroke={MAIN_COLORS[1]}
            strokeWidth={1}
            dot={false}
            order={0}
          />
        );
      } else {
        allCharts.push(
          <Bar
            key={metric}
            // yAxisId="left"
            dataKey={metric}
            fill={MAIN_COLORS[0]}
            cursor="pointer"
            order={1}
          />
        );
      }
    });
    return allCharts;
  };

  KeyStatsItem = ({ title, data, metricsByDay }) => {
    const metricKey = title.toLowerCase().replace(" ", "_");
    const formattedMetricKey =
      this.widgetTitles[title] || this.formatMetricKey(metricKey);

    const metrics =
      metricsByDay[metricKey].length > 0
        ? Object.keys(metricsByDay[metricKey][0])
        : [];
    metrics.splice(metrics.indexOf("date"), 1);

    return (
      <div className="col-lg-3 col-sm-6 col-12 key-stats--item mb-4">
        <div className="row row--header">
          <div className="col-12">
            <Heading size={5}>{formattedMetricKey}</Heading>
            <Metric
              {...data.metrics[metricKey]}
              type={metricKey.includes("unique") ? "number" : "percentage"}
              size={"md"}
              format={metricKey.includes("unique") ? null : "%0.00"}
            />
          </div>
        </div>
        <ResponsiveContainer height={150} width="100%" className="mt-5">
          <ComposedChart data={metricsByDay[metricKey]}>
            <Tooltip
              cursor={{ fill: "#eee", opacity: 0.5 }}
              content={({ active, payload, label }) => (
                <this.CustomTooltip {...{ active, payload, label, title }} />
              )}
            />
            {this.renderCharts(metrics)}

            <XAxis dataKey="date" hide />
            <YAxis
              // yAxisId="left"
              axisLine={false}
              tickLine={false}
              mirror={true}
              // tick={<CustomizedYAxisTick />}
              hide
            />
            <YAxis
              orientation={"right"}
              yAxisId={"right"}
              axisLine={false}
              tickLine={false}
              mirror={true}
              hide
              tick={<CustomizedYAxisTickRight />}
              {...(["ctr", "ctd"].filter((m) => title.includes(m)).length > 0
                ? { domain: [0, 0.5] }
                : null)}
            />
          </ComposedChart>
        </ResponsiveContainer>
      </div>
    );
  };

  generateDataForExport = () => {
    const data = this.props.data.metrics;
    let formattedData = [];

    Object.keys(data).map((i) =>
      formattedData.push({
        Metric: i
          .replace("_", " ")
          .split(/ /g)
          .map(
            (word) =>
              `${word.substring(0, 1).toUpperCase()}${word.substring(1)}`
          )
          .join(" "),
        Value: selectFormat("number", data[i].value, "0,0"),
      })
    );

    return formattedData;
  };

  getExportData = () => ({
    data: this.generateDataForExport(),
    opts: {
      type: "other",
    },
  });

  render() {
    const { data, loading } = this.props;

    if (loading) {
      return <Loader minHeight={340} col={"col-12 col-md-6"} />;
    }

    const metricsOrder = [
      "delivered_rate",
      "open_rate",
      "ctr",
      "converted_unique",
      "ctd",
    ];
    const orderedMetrics = metricsOrder.filter(
      (metric) => data.metrics[metric]
    );

    return (
      <div className="row key-stats border-between-metrics">
        {orderedMetrics.map((key) => (
          <this.KeyStatsItem
            key={key}
            title={key}
            data={data}
            metricsByDay={data.metricsByDay}
          />
        ))}
      </div>
    );
  }
}

export default LeeSummaryMetrics;
